import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { DetailTreeNav, type TreeNode } from '@/components/layout/DetailTreeNav';
import { ToastContainer } from '@/components/common/Toast';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useToast } from '@/lib/hooks/useToast';
import {
  InvoiceInlineDetail, InvoiceModal, RecordPaymentModal, InvoiceDetailModal,
} from '@/components/invoices';
import {
  getInvoice, getInvoices, getInvoicesByClient,
  updateInvoice, deleteInvoice, sendInvoice, recordPayment, invoicePath,
  type Invoice, type InvoiceDetail, type CreateInvoiceData, type UpdateInvoiceData, type RecordPaymentData,
} from '@/lib/api/invoices';
import { jobPath, jobSectionPath } from '@/lib/utils/navPaths';

const formatDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';

/**
 * Unified invoice detail — the single canonical destination for any invoice across
 * the app (global /invoices, a client's invoices, a job's invoices all route here).
 * URL encodes the recursive path: /clients/:clientId/jobs/:jobId/invoices/:invoiceId
 * (jobId optional). The left rail lists sibling invoices for quick switching; the
 * breadcrumb shows the clients › jobs › invoices chain.
 */
export const InvoiceDetailPage = () => {
  const { clientId, jobId, invoiceId } = useParams<{ clientId: string; jobId?: string; invoiceId: string }>();
  const navigate = useNavigate();
  const { toast, toasts, removeToast } = useToast();

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [siblings, setSiblings] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      setInvoice(await getInvoice(Number(invoiceId)));
    } catch {
      toast.error('Failed to load invoice.');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
    // toast is a fresh object each render (useToast) — excluding it prevents an infinite reload loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  useEffect(() => { loadInvoice(); }, [loadInvoice]);

  // Sibling invoices for the left rail — scoped to the job if present, else the client.
  useEffect(() => {
    (async () => {
      try {
        if (jobId) {
          const res = await getInvoices({ job_id: Number(jobId), limit: 200 });
          setSiblings(Array.isArray(res?.invoices) ? res.invoices : []);
        } else if (clientId) {
          const byClient = await getInvoicesByClient(Number(clientId));
          setSiblings(Array.isArray(byClient) ? byClient : []);
        }
      } catch {
        setSiblings([]);
      }
    })();
  }, [jobId, clientId]);

  const handleSend = async () => {
    if (!invoice) return;
    try {
      await sendInvoice(invoice.id);
      toast.success('Invoice marked as sent');
      loadInvoice();
    } catch { toast.error('Failed to send invoice'); }
  };

  const handleSaveEdit = async (data: CreateInvoiceData | UpdateInvoiceData, id?: number) => {
    if (!id) return;
    await updateInvoice(id, data as UpdateInvoiceData);
    toast.success('Invoice updated');
    setShowEdit(false);
    loadInvoice();
  };

  const handleSubmitPayment = async (data: RecordPaymentData) => {
    if (!invoice) return;
    await recordPayment(invoice.id, data);
    toast.success('Payment recorded');
    setShowPayment(false);
    loadInvoice();
  };

  const handleDelete = async () => {
    if (!invoice) return;
    setConfirmDelete(false);
    try {
      await deleteInvoice(invoice.id);
      toast.success('Invoice deleted');
      navigate(jobId ? jobSectionPath(clientId, jobId, 'invoices') : `/clients/${clientId}`);
    } catch { toast.error('Failed to delete invoice'); }
  };

  const treeNodes: TreeNode[] = [
    {
      key: 'invoices', label: 'Invoices', icon: 'receipt_long', count: siblings.length,
      children: siblings.map(s => ({ key: `inv:${s.id}`, label: s.invoice_number, icon: 'receipt' })),
    },
  ];

  const onTreeSelect = (key: string) => {
    if (key.startsWith('inv:')) {
      const sid = key.slice('inv:'.length);
      const s = siblings.find(x => String(x.id) === sid);
      if (s) navigate(invoicePath(s));
    } else {
      navigate(jobId ? jobSectionPath(clientId, jobId, 'invoices') : `/clients/${clientId}`);
    }
  };

  return (
    <MainLayout
      noPadding
      clientListPanel={
        <DetailTreeNav
          title={invoice?.client_name || 'Client'}
          subtitle={invoice?.job_title || 'Invoices'}
          back={{
            label: jobId ? 'Back to job' : 'Back to client',
            onClick: () => navigate(jobId ? jobPath(clientId, jobId) : `/clients/${clientId}`),
          }}
          nodes={treeNodes}
          activeKey={`inv:${invoiceId}`}
          onSelect={onTreeSelect}
          defaultExpanded={['invoices']}
        />
      }
    >
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="p-4 md:p-6">
        {/* Breadcrumb — recursive path clients › jobs › invoices */}

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
              Loading invoice…
            </div>
          ) : !invoice ? (
            <div className="text-center py-16 text-gray-500">Invoice not found.</div>
          ) : (
            <InvoiceInlineDetail
              invoice={invoice}
              onBack={() => navigate(jobId ? jobSectionPath(clientId, jobId, 'invoices') : `/clients/${clientId}`)}
              onEdit={() => setShowEdit(true)}
              onSend={handleSend}
              onRecordPayment={() => setShowPayment(true)}
              onOpenFull={() => setShowFull(true)}
              formatDate={formatDate}
            />
          )}
        </div>
      </div>

      {/* Edit */}
      {invoice && (
        <InvoiceModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          onSave={handleSaveEdit}
          invoice={{ ...invoice, items: invoice.items }}
          clientId={invoice.client_id}
          clientName={invoice.client_name || ''}
          jobs={invoice.job_id ? [{ id: invoice.job_id, title: invoice.job_title || `Job #${invoice.job_id}` }] : []}
        />
      )}

      {/* Record payment */}
      <RecordPaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onSubmit={handleSubmitPayment}
        invoice={invoice}
      />

      {/* Full view (PDF / print / attachments) */}
      <InvoiceDetailModal
        isOpen={showFull}
        onClose={() => setShowFull(false)}
        invoice={invoice}
        onEdit={() => { setShowFull(false); setShowEdit(true); }}
        onSend={handleSend}
        onRecordPayment={() => { setShowFull(false); setShowPayment(true); }}
        onDelete={() => { setShowFull(false); setConfirmDelete(true); }}
        onRefresh={(updated) => setInvoice(updated)}
      />

      {confirmDelete && (
        <ConfirmDialog
          title="Delete Invoice"
          message="Are you sure you want to delete this invoice? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </MainLayout>
  );
};
